import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, snowflakeV2AuthTypes, getConnectorV2Name, snowflakeAdditionalFieldsKeyOptions, Type } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { snowflakeCommonControls, dynamicPasswordControl, roleControl, nameControl } from './snowflake-metadata';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { isMooncakeEnvironment } from 'environments/environment.util';
import { snowflakeVersioning } from 'app/shared/constants/variable-constants';
import { cdcDestination } from 'app/registries/consts/metadata-consts';

const dataflowSnowFlake: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.SnowflakeV2,
    settings: adfModelTypeMap.snowflakeV2.dataflowSettings,
    useInlineProjection: true,
    enableImportSchemaInSink: true,
    enableAlterRow: true
};
const privateKeyControl: ControlMetadata[] = [
    nameControl,
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'privateKey',
        label: stringResources.ModelName_PrivateKey,
        child: {
            type: 'parameterizable',
            attribute: 'privateKey',
            disableAddParameter: true,
            child: {
                type: 'key-file-content',
                label: stringResources.ModelName_PrivateKey,
                required: true,
                isLabelVertical: true,
                readAsText: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            }
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.Snowflake_DisplayText,
            stringResources.ModelName_PrivateKey)
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'privateKeyPassphrase',
        label: stringResources.ModelName_PrivateKeyPassphrase,
        child: {
            type: 'parameterizable',
            attribute: 'privateKeyPassphrase',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_PrivateKeyPassphrase,
                isLabelVertical: true,
            }
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.Snowflake_DisplayText,
            stringResources.ModelName_PrivateKeyPassphrase)
    }
];

const aadServicePrincipalControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'tenantId',
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            label: stringResources.ModelName_Tenant,
            isLabelVertical: true
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'clientId',
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            label: stringResources.ModelName_ClientId,
            isLabelVertical: true
        }
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'clientSecret',
        label: stringResources.ModelName_ClientSecret,
        child: {
            type: 'parameterizable',
            attribute: 'clientSecret',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_ClientSecret,
                required: true,
                isLabelVertical: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            }
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Snowflake_DisplayText),
            stringResources.ModelName_ClientSecret)
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'scope',
            required: true,
            label: stringResources.Scope,
            isLabelVertical: true
        }
    }
];

const authenticationOptionsInput: ControlMetadata[] = [
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        description: stringResources.Snowflake_AuthType_Desc_v2,
        required: true,
        options: [
            {
                label: stringResources.Basic_DisplayText,
                value: snowflakeV2AuthTypes.Basic
            },
            {
                label: stringResources.AuthenticationType_KeyPair,
                value: snowflakeV2AuthTypes.KeyPair,
            },
            {
                label: stringResources.AuthenticationType_ServicePrincipal,
                value: snowflakeV2AuthTypes.AADServicePrincipal,
                hidden: !GlobalVariables.enableSnowflakeV2SPN
            }
        ],
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        defaultValue: snowflakeV2AuthTypes.Basic,
        cases: [
            {
                caseValue: snowflakeV2AuthTypes.Basic,
                children: [
                    nameControl,
                    dynamicPasswordControl
                ]
            },
            {
                caseValue: snowflakeV2AuthTypes.KeyPair,
                children: privateKeyControl
            },
            {
                caseValue: snowflakeV2AuthTypes.AADServicePrincipal,
                children: aadServicePrincipalControls
            }
        ]
    }
];

function hostControl(isMooncake: boolean = false): ControlMetadata {
    return {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'host',
            label: stringResources.ModelName_Host,
            placeholder: isMooncake ? 'contoso.snowflakecomputing.cn' : 'contoso.snowflakecomputing.com',
            isLabelVertical: true,
            required: false
        }
    };
}

function getSnowflakeControls(): ControlMetadata[] {
    return [
        {
            type: 'radio',
            bind: 'version',
            label: stringResources.VersionLabel,
            description: stringResources.versionDesc,
            options: snowflakeVersioning,
            defaultValue: '1.1',
            isLabelVertical: true
        },
        ...snowflakeCommonControls(true),
        ...authenticationOptionsInput,
        roleControl,
        hostControl(isMooncakeEnvironment()),
        {
            type: 'parameter-config',
            bind: 'parameterizableAdditionalFields',
            label: stringResources.AdditionalConnectionStringFields,
            isLayoutVertical: true,
            isKeyDropdown: true,
            keyOptions: snowflakeAdditionalFieldsKeyOptions,
            keyOptionHasType: true,
            dynamicInputTypes: true,
            dynamicInputTypesParameterizable: true,
            keyLabel: stringResources.PropertyName,
            defaultWidth: 250
        }
    ];
}

export const snowflakeV2Metadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: __IS_FABRIC_DPE__ ? adfModelTypeMap.snowflake.linkedServiceType : adfModelTypeMap.snowflakeV2.linkedServiceType,
        displayName: getConnectorV2Name(stringResources.Snowflake_DisplayText, __IS_FABRIC_DPE__),
        linkedServiceNamePrefix: adfModelTypeMap.snowflake.linkedServiceType,
        supportedAuthKindsInTrident: [
            ConnectionAuthKind.Basic,
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.KeyPair,
            ConnectionAuthKind.WorkspaceIdentity
        ],
        distinguishTableView: !!__IS_FABRIC_DPE__,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: ResourceKind.Snowflake
        },
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        cdc: {
            supportCDC: true,
            supportedCDCDestinations: cdcDestination,
            tableIcon: {
                regular: 'icon-table_text_cdc_20_regular',
                filled: 'icon-table_text_cdc_20_filled'
            },
            GA: true
        },
        supportMultipleIncrementalColumns: true,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-SnowFlake'
        },
        SHIRVersion: {
            '1.1': '5.52.9203.1'
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportOPDGInMirror: true,
        supportVNETInMirror: true,
        supportScriptActivity: true,
        scriptActivityParamDisabledDesc: stringResources.snowflakev2_scriptParamDisabledDesc,
        supportedInTrident: true,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        dataflowSource: [dataflowSnowFlake],
        dataflowSink: [dataflowSnowFlake],
        isFirstClass: true,
        supportFirstClassParameterExperience: true,
        category: ['Database'],
        supportDatasetV2: true,
        dataflowSinkStagingSchema: true,
        supportPreCopyScript: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        copySinkTypes: [__IS_FABRIC_DPE__ ? adfModelTypeMap.snowflake.copySinkType : adfModelTypeMap.snowflakeV2.copySinkType],
        copySourceTypes: [__IS_FABRIC_DPE__ ? adfModelTypeMap.snowflake.copySourceType : adfModelTypeMap.snowflakeV2.copySourceType],
        controls: getSnowflakeControls(),
        additionalPropertiesInFabric: __IS_FABRIC_DPE__ ? [
            {
                label: 'Role',
                value: 'role',
                type: Type.String,
                options: ['ORGADMIN', 'ACCOUNTADMIN', 'SYSADMIN', 'USERADMIN', 'SECURITYADMIN', 'PUBLIC']
            }
        ].concat(
            GlobalVariables.connectionPropertiesForSnowflake ? [
                {
                    label: 'Use utc timestamps',
                    value: 'useUtcTimestamps',
                    type: Type.Boolean,
                    options: undefined
                },
                {
                    label: 'Schema',
                    value: 'schema',
                    type: Type.String,
                    options: undefined
                }
            ] : []
        ) : undefined
    };
    return metadata;
};
